import { StateCreator } from 'zustand';
import { BusinessPartnerScope } from '@/api/roles/roles.types.ts';
import { ACCOUNT_TYPE, AccountType } from '@/constants/account-type';

/**
 * Cached authorization snapshot from `/v1/auth/me`. This is the single source
 * of truth for the frontend's view of who the user is:
 *
 * - `accountType` — `INTERNAL` or `EXTERNAL`.
 * - `isAdmin` — derived on the backend from the assigned `Administrator`
 *   system role; we never recompute it here.
 * - `permissions` — the user's effective permission list.
 * - `businessPartnerScope` — `ALL` or `OWN`.
 * - `salesPersonCode` — the user's own SAP sales-person code, used to default
 *   scope-aware UI filters (e.g. the business-partners list) to "my partners".
 *
 * Everything is refilled by `useMe()`; `resetAuthorization()` clears it on
 * sign-out.
 */
export interface AuthorizationSlice {
  accountType: AccountType;
  setAccountType: (accountType: AccountType) => void;
  isAdmin: boolean;
  setIsAdmin: (isAdmin: boolean) => void;
  permissions: string[];
  setPermissions: (permissions: string[]) => void;
  businessPartnerScope: BusinessPartnerScope;
  setBusinessPartnerScope: (scope: BusinessPartnerScope) => void;
  salesPersonCode: number | null;
  setSalesPersonCode: (salesPersonCode: number | null) => void;
  resetAuthorization: () => void;
}

export const createAuthorizationSlice: StateCreator<AuthorizationSlice> = (
  set,
) => ({
  accountType: ACCOUNT_TYPE.EXTERNAL,
  setAccountType: (accountType) => set({ accountType }),
  isAdmin: false,
  setIsAdmin: (isAdmin) => set({ isAdmin }),
  permissions: [],
  setPermissions: (permissions) => set({ permissions }),
  businessPartnerScope: 'ALL',
  setBusinessPartnerScope: (businessPartnerScope) =>
    set({ businessPartnerScope }),
  salesPersonCode: null,
  setSalesPersonCode: (salesPersonCode) => set({ salesPersonCode }),
  resetAuthorization: () =>
    set({
      accountType: ACCOUNT_TYPE.EXTERNAL,
      isAdmin: false,
      permissions: [],
      businessPartnerScope: 'ALL',
      salesPersonCode: null,
    }),
});
